import { inject, Injectable, OutputEmitterRef } from '@angular/core';
import * as Leaflet from 'leaflet';
import '@geoman-io/leaflet-geoman-free';
import { MapLayer, MapMarker, MapInput, LayerEditEvent } from '../interfaces';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';
import { SupportUserService } from '@shared/services/support-user.service';
@Injectable()
export class PolyLinesService {
  private translate = inject(TranslateService);
  private supportUserService = inject(SupportUserService);

  polyLineLayers: MapLayer[] = [];
  editablePolylines: L.FeatureGroup = Leaflet.featureGroup();
  helpControl: L.Control | undefined;

  private isNewPolylinePoint = false;
  private polylineCreateReady = false;
  private globalEditModeOn = false;

  setPmDrawTranslations(map: L.Map): void {
    map.pm.setLang('en', {
      actions: {
        finish: this.translate.instant('General.StopEditing'),
        removeLastVertex: this.translate.instant('General.RemoveLastPoint'),
        cancel: this.translate.instant('General.Cancel'),
      },
      buttonTitles: {
        drawPolyButton: this.translate.instant('General.DrawSegment'),
        editButton: this.translate.instant('General.EnableEdit'),
        drawLineButton: this.translate.instant('General.DrawPolyline'),
      },
      tooltips: {
        continueLine: this.translate.instant('General.ContinueLine'),
        finishLine: this.translate.instant('General.FinishLine'),
        placeCircleMarker: this.translate.instant('General.PlaceCircleMarker'),
        firstVertex: this.translate.instant('General.FirstPoint'),
      },
    });
  }

  clearPolylineLayers(map: L.Map): void {
    this.polyLineLayers.forEach(layer => {
      map.removeLayer(layer);
    });
    // keeping the array reference
    this.polyLineLayers.splice(0, this.polyLineLayers.length);
  }

  removePolylineAtIndex(index: number): void {
    this.polyLineLayers.splice(index, 1);
  }

  drawMapLines(
    mapInputs: MapInput[] | null | undefined,
    map: Leaflet.Map,
    ignoreDrawEvents = false,
    polylineEditEmitter?: OutputEmitterRef<LayerEditEvent>,
    polylinePointRemoveEmitter?: OutputEmitterRef<LayerEditEvent>,
    polylineAddEmitter?: OutputEmitterRef<LayerEditEvent>,
    polyEditMarkerClicked?: OutputEmitterRef<LayerEditEvent>,
    polylineClicked?: OutputEmitterRef<MapLayer>
  ): MapLayer[] {
    const addedPolylines: MapLayer[] = [];
    for (let i = 0; i < (mapInputs || []).length; i++) {
      const mapInput = (mapInputs || [])[i];
      if (!mapInput.isRouteInput) continue;

      const poly = this.createPolyline(mapInput);
      if (!poly) continue;
      poly.on('click', (event: Leaflet.LeafletMouseEvent) => {
        polylineClicked?.emit(event.target);
      });

      this.setupPolylineInteractions(poly, mapInput);
      this.addEditablePolylineIfNeeded(poly, mapInput);

      this.polyLineLayers.push(poly);
      addedPolylines.push(poly);
    }

    if (addedPolylines.length) {
      this.addPolylinesToMap(addedPolylines, map);
    }

    if (!ignoreDrawEvents) {
      this.setupPolylineDrawEvents(
        map,
        polylineEditEmitter,
        polylinePointRemoveEmitter,
        polylineAddEmitter,
        polyEditMarkerClicked
      );
    }

    return addedPolylines;
  }

  /** Create a Leaflet polyline from MapInput */
  private createPolyline(mapInput: MapInput): MapLayer | null {
    const points = mapInput.points;
    if (!points || points.length < 2) return null;

    const latLngs = points.map(p => Leaflet.latLng(p.lat, p.lng));
    const segmentColor = mapInput.routeColor || 'blue';
    const segmentOpacity = points.every(p => p.options?.opacity === 1)
      ? 0
      : undefined;

    const poly: MapLayer = Leaflet.polyline(latLngs, {
      color: segmentColor,
      opacity: segmentOpacity,
      smoothFactor: 0,
      weight: 6,
      interactive: mapInput.enableHoverAnimation,
    });

    poly.extraData = {
      /** find any segment ID from the same line since they will all be the same */
      polylineSegmentId: points.find(p => p.extraData?.polylineSegmentId)
        ?.extraData?.polylineSegmentId,
      extraDataForLayerGetterFn: points.find(
        p => p.extraData?.extraDataForLayerGetterFn
      )?.extraData?.extraDataForLayerGetterFn,
      polylineData: points,
    };

    return poly;
  }

  /** Setup hover and right-click interactions on a polyline */
  private setupPolylineInteractions(poly: MapLayer, mapInput: MapInput): void {
    const segmentColor = (poly as L.Polyline).options.color as string;

    if (mapInput.enableHoverAnimation) {
      poly.on('mouseover', () => {
        (poly as Leaflet.Polyline).setStyle({ color: 'red', weight: 8 });
      });

      poly.on('mouseout', () => {
        (poly as Leaflet.Polyline).setStyle({ color: segmentColor, weight: 6 });
      });
    }

    if (mapInput.layerRightClickFn) {
      poly.on('contextmenu', (e: Leaflet.LeafletMouseEvent) =>
        mapInput.layerRightClickFn!(e)
      );
    }
  }

  /** Add polyline to editable layer group if any point is editable */
  private addEditablePolylineIfNeeded(
    poly: MapLayer,
    mapInput: MapInput
  ): void {
    if (mapInput.points?.some(p => p.extraData?.editable)) {
      this.editablePolylines.addLayer(poly);
    } else {
      // making sure editing disabled for not editable polylines
      (poly as Leaflet.Polyline).pm.setOptions({ allowEditing: false });
    }
  }

  /** Add all polylines to the map */
  private addPolylinesToMap(polylines: MapLayer[], map: Leaflet.Map): void {
    polylines.forEach(poly => poly.addTo(map));
  }

  private setupPolylineDrawEvents(
    mapComponent: L.Map,
    polylineEditEmitter?: OutputEmitterRef<LayerEditEvent>,
    polylinePointRemoveEmitter?: OutputEmitterRef<LayerEditEvent>,
    polylineAddEmitter?: OutputEmitterRef<LayerEditEvent>,
    polyEditMarkerClicked?: OutputEmitterRef<LayerEditEvent>
  ): void {
    const editablePolylinesLayers =
      this.editablePolylines.getLayers() as L.Polyline[];

    const editingEnabled = !!editablePolylinesLayers.length;
    const addingEnabled = !!polylineAddEmitter;

    /** disable all controls, all editing is enabled by default */
    mapComponent.pm.addControls({
      position: 'topright',
      drawPolyline: addingEnabled,
      editMode: editingEnabled,
      dragMode: false,
      cutPolygon: false,
      drawMarker: false,
      drawCircleMarker: false,
      drawCircle: false,
      drawPolygon: false,
      drawRectangle: false,
      drawText: false,
      rotateMode: false,
      removalMode: false,
    });

    mapComponent.pm.setGlobalOptions({
      markerEditable: false,
    });

    if (polylineAddEmitter) {
      this.addPolylineCreatedEventListener(polylineAddEmitter, mapComponent);
    }

    editablePolylinesLayers.forEach(polyLayer => {
      polyLayer.pm.setOptions({
        draggable: true,
        snappable: false,
        removeVertexOn: 'contextmenu',
        addVertexOn: 'contextmenu',
        allowCutting: !!polyEditMarkerClicked,
      });

      if (polylineEditEmitter) {
        this.addMarkerDragEndEventListener(polyLayer, polylineEditEmitter);
      }

      this.addVertexAddedEventListener(polyLayer);

      if (polylinePointRemoveEmitter) {
        this.addVertexRemovedEventListener(
          polyLayer,
          polylinePointRemoveEmitter
        );
      }

      if (polyEditMarkerClicked) {
        this.addVertexMarkerRightClickedEventListener(
          polyLayer,
          polyEditMarkerClicked
        );
      }
    });

    this.listenToGlobalEditModeToggle(mapComponent);
    if (editingEnabled || addingEnabled) {
      this.addMapEditingHelpControl(mapComponent);
    }
  }

  private listenToGlobalEditModeToggle(mapComponent: Leaflet.Map): void {
    mapComponent.on(
      'pm:globaleditmodetoggled',
      (event: { enabled: boolean; map: L.Map }) => {
        this.globalEditModeOn = !!event.enabled;
        if (this.helpControl) {
          event.map.removeControl(this.helpControl);
          this.helpControl = undefined;
        }

        this.addMapEditingHelpControl(event.map);
      }
    );
  }

  private addVertexMarkerRightClickedEventListener(
    polyLayer: MapLayer,
    polyEditMarkerClicked: OutputEmitterRef<LayerEditEvent>
  ): void {
    polyLayer.on(
      'pm:vertexclick',
      (e: {
        layer: MapLayer;
        // incorrect Leaflet.PM interface, actually returns number array
        indexPath: any;
        markerEvent: { latLng: Leaflet.LatLng };
        shape: Leaflet.PM.SUPPORTED_SHAPES;
      }) => {
        const polylineData = polyLayer.extraData?.polylineData || [];
        polyEditMarkerClicked.emit({
          index: e.indexPath[0],
          markers: [polylineData[e.indexPath[0]]],
        });
      }
    );
  }

  private handleNewPolyPointAdd(
    e: {
      layer: L.Layer & MapLayer & L.Polyline;
      indexPath: number[];
      markerEvent: any;
      shape: Leaflet.PM.SUPPORTED_SHAPES;
      intersectionReset: boolean;
    },
    polyLayer: MapLayer
  ): void {
    const { indexPath } = e;
    const data = (polyLayer as MapLayer).extraData?.polylineData || [];
    const latlng = e.layer.getLatLngs()[indexPath[0]] as Leaflet.LatLng;
    const point: MapMarker = {
      lat: latlng.lat,
      lng: latlng.lng,
      extraData: {
        polylineSegmentId: (polyLayer as MapLayer).extraData?.polylineSegmentId,
      },
    };

    data.splice(indexPath[0], 0, point);

    const extraDataForLayerGetterFn = (polyLayer as MapLayer).extraData
      ?.extraDataForLayerGetterFn;
    if (extraDataForLayerGetterFn) {
      extraDataForLayerGetterFn({
        markers: [point],
        index: Number(indexPath[0] + 1),
      })
        .pipe(take(1))
        .subscribe({
          next: extraData => {
            data[indexPath[0]].extraData = extraData;
          },
        });
    }
  }

  private addVertexAddedEventListener(polyLayer: MapLayer): void {
    polyLayer.on('pm:vertexadded', () => {
      this.isNewPolylinePoint = true;
    });
  }

  private addVertexRemovedEventListener(
    polyLayer: MapLayer,
    polylinePointRemoveEmitter: OutputEmitterRef<LayerEditEvent>
  ): void {
    polyLayer.on(
      'pm:vertexremoved',
      (e: {
        layer: L.Layer & MapLayer;
        indexPath: number;
        marker: L.Marker;
        shape: Leaflet.PM.SUPPORTED_SHAPES;
      }) => {
        const index = e.indexPath;
        const lastCommitted: MapMarker[] =
          polyLayer.extraData?.polylineData || [];

        const removedPoint = lastCommitted[index];
        const updated = [...lastCommitted];
        updated.splice(index, 1);

        if (polyLayer.extraData) {
          polyLayer.extraData.polylineData = updated;
        }

        polylinePointRemoveEmitter.emit({
          markers: [removedPoint],
          index: index + 1,
        });
      }
    );
  }

  private addPolylineCreatedEventListener(
    polylineAddEmitter: OutputEmitterRef<LayerEditEvent>,
    map: L.Map
  ): void {
    if (this.polylineCreateReady) return;

    map.on(
      'pm:create',
      (e: { shape: Leaflet.PM.SUPPORTED_SHAPES; layer: MapLayer }) => {
        if (e.shape !== 'Line') return;

        this.polyLineLayers.push(e.layer);
        polylineAddEmitter.emit({
          markers: (e.layer as L.Polyline).getLatLngs() as L.LatLng[],
        });
      }
    );
    this.polylineCreateReady = true;
  }

  private addMarkerDragEndEventListener(
    polyLayer: MapLayer | any,
    polylineEditEmitter: OutputEmitterRef<LayerEditEvent>
  ): void {
    polyLayer.on(
      'pm:markerdragend',
      (e: {
        layer: L.Layer & MapLayer & L.Polyline;
        indexPath: number[];
        markerEvent: unknown;
        shape: Leaflet.PM.SUPPORTED_SHAPES;
        intersectionReset: boolean;
      }) => {
        if (this.isNewPolylinePoint) {
          this.isNewPolylinePoint = false;
          this.handleNewPolyPointAdd(e, polyLayer);
          return;
        }

        const editedLatLngs: L.LatLng[] = e.layer.getLatLngs() as L.LatLng[];
        const lastCommitted: MapMarker[] =
          e.layer.extraData?.polylineData || [];

        const editedPoints: MapMarker[] = editedLatLngs.map((ll, idx) => {
          const prev = lastCommitted[idx];
          return {
            lat: ll.lat,
            lng: ll.lng,
            extraData: prev.extraData,
          };
        });

        // find only changed points (position changed or newly added/removed)
        const changed: MapMarker[] = [];
        const maxLen = Math.max(lastCommitted.length, editedPoints.length);
        for (let i = 0; i < maxLen; i++) {
          const prev: MapMarker | undefined = lastCommitted[i] as
            | MapMarker
            | undefined;
          const cur: MapMarker | undefined = editedPoints[i] as
            | MapMarker
            | undefined;

          // added vertex
          if (!prev && cur) {
            changed.push(cur);
            continue;
          }
          // removed vertex
          if (prev && !cur) {
            changed.push(prev);
            continue;
          }

          // moved vertex: compare coords with a small epsilon
          const moved =
            prev &&
            cur &&
            (Math.abs(prev.lat - cur.lat) > 1e-8 ||
              Math.abs(prev.lng - cur.lng) > 1e-8);
          if (moved) {
            changed.push(cur);
          }
        }

        e.layer.extraData!.polylineData = editedPoints;

        polylineEditEmitter.emit({ markers: changed });
      }
    );
  }

  private addMapEditingHelpControl(mapComponent: Leaflet.Map): void {
    if (this.helpControl) return;

    this.supportUserService.isSupportUser().subscribe(extendedEditEnabled => {
      const HelpControl = Leaflet.Control.extend({
        options: {
          position: 'topleft',
        },

        onAdd: () => {
          const container = Leaflet.DomUtil.create(
            'div',
            'leaflet-control leaflet-bar leaflet-help-control'
          );

          container.innerHTML = `
            <div class="geo-help-title dv-headline-sm">${this.translate.instant('Map.MapEditing')}</div>
              <ul class="geo-help-list dv-body-sm">
                ${
                  this.globalEditModeOn
                    ? `<li>${this.translate.instant('Map.Editing.DragText')}</li>
                      <li>${this.translate.instant('Map.Editing.DragMiddleToAdd')}</li>
                      <li>${this.translate.instant('Map.Editing.RightClickText')}</li>`
                    : ''
                }
                ${extendedEditEnabled && this.globalEditModeOn ? `<li>${this.translate.instant('Map.Editing.SplitSegmentText')}</li>` : ''}
                ${
                  extendedEditEnabled
                    ? `<li>${this.translate.instant('Map.Editing.DeleteSegmentText')}</li>
                      <li>${this.translate.instant('Map.Editing.SegmentMergeText')}</li>`
                    : ''
                }
                ${
                  !extendedEditEnabled && !this.globalEditModeOn
                    ? `<li>${this.translate.instant('Map.Editing.EnableEditToSeeInstructions')}</li>`
                    : ''
                }
              </ul>`;

          // prevent map interactions when hovering/clicking the box
          Leaflet.DomEvent.disableClickPropagation(container);
          Leaflet.DomEvent.disableScrollPropagation(container);

          return container;
        },
      });

      this.helpControl = new HelpControl();
      mapComponent.addControl(this.helpControl);
    });
  }
}
